"use client";

import FieldsFormatter from "@/components/organisms/admin/FieldsFormatter";
import TableFormatter from "@/components/organisms/admin/TableFormatter";
import { Button } from "@/components/atoms/Button";
import type { FormProps, MetadataItem } from "./types";
import { useForm8 } from "./hooks";
import { CategoryManager } from "./components";

/**
 * Form_8 - Program Fee V2
 *
 * A flexible admin form for managing fee sections with:
 * - Component-level settings (titles, gradients, CTA, colors)
 * - metadata array (What You Get checklist items)
 * - metadata_1 array (Feature cards with icon + description)
 * - categories array (Fee tabs, each with their own metadata/metadata_1)
 *
 * Data is saved as a single JSON payload, making it flexible and
 * adaptable for different fee section layouts.
 */
const Form_8 = ({
  selectedSection,
  onSave,
  pageId,
  pageComponentId,
  variantDetails,
}: FormProps) => {
  const {
    formState,
    componentFieldsConfig,
    handleComponentFieldsUpdate,
    handleMetadataUpdate,
    handleMetadata1Update,
    handleAddCategory,
    handleDeleteCategory,
    handleUpdateCategory,
    handleUpdateCategoryFields,
    handleUpdateCategoryMetadata,
    handleSave,
  } = useForm8({
    selectedSection,
    pageId,
    pageComponentId,
    variantDetails,
  });

  if (!variantDetails) return null;

  const metadataFields = variantDetails.metadataFields || [];
  const metadata1Fields = variantDetails.metadata1Fields || [];
  const categoryMetadataFields = variantDetails.categoryMetadataFields || [];

  return (
    <div>
      {/* Section Header */}
      <div className="mb-6">
        <h3 className="font-semibold mb-2">{variantDetails.name}</h3>
        <p className="text-sm text-gray-500 mb-4">
          {variantDetails.description}
        </p>
      </div>

      {/* Component Settings */}
      {componentFieldsConfig.length > 0 && (
        <div className="border-t pt-6 mb-6">
          <h4 className="font-semibold mb-4 flex items-center gap-2">
            <span>📝</span> Content Settings
          </h4>
          <FieldsFormatter
            data={formState.componentFields}
            fields={componentFieldsConfig}
            updateParent={true}
            onChange={handleComponentFieldsUpdate}
            validationErrors={formState.componentFieldErrors}
            validationMessages={formState.componentFieldMessages}
            className="p-0 bg-transparent"
          />
        </div>
      )}

      {/* What You Get Items (metadata) */}
      {metadataFields.length > 0 && (
        <div className="border-t pt-6 mb-6">
          <h4 className="font-semibold mb-4 flex items-center gap-2">
            <span>✓</span> What You Get Items
          </h4>
          <TableFormatter
            data={formState.metadata}
            fields={metadataFields}
            onDataUpdate={(data) => handleMetadataUpdate(data as unknown as MetadataItem[])}
          />
        </div>
      )}

      {/* Feature Cards (metadata_1) */}
      {metadata1Fields.length > 0 && (
        <div className="border-t pt-6 mb-6">
          <h4 className="font-semibold mb-4 flex items-center gap-2">
            <span>🏆</span> Feature Cards
          </h4>
          <TableFormatter
            data={formState.metadata_1}
            fields={metadata1Fields}
            onDataUpdate={(data) => handleMetadata1Update(data as unknown as MetadataItem[])}
          />
        </div>
      )}

      {/* Repeater (fee categories by default; configurable per variant) */}
      <div className="border-t pt-6 mb-6">
        <h4 className="font-semibold mb-4 flex items-center gap-2">
          <span>{variantDetails.repeaterSectionIcon || "💰"}</span>{" "}
          {variantDetails.repeaterSectionLabel || "Fee Categories"}
        </h4>
        <CategoryManager
          categories={formState.categories}
          categoryMetadataFields={categoryMetadataFields}
          onAddCategory={handleAddCategory}
          onDeleteCategory={handleDeleteCategory}
          onUpdateCategory={handleUpdateCategory}
          onUpdateCategoryFields={handleUpdateCategoryFields}
          onUpdateCategoryMetadata={handleUpdateCategoryMetadata}
          categoryFieldsConfig={variantDetails.categoryFieldsConfig}
          itemsKey={variantDetails.itemsKey}
          showColors={variantDetails.showCategoryColors !== false}
          titleLabel={variantDetails.categoryTitleLabel}
          itemsLabel={variantDetails.categoryItemsLabel}
          itemsHint={variantDetails.categoryItemsHint}
          addLabel={variantDetails.addCategoryLabel}
          categorySingularLabel={variantDetails.categorySingularLabel}
        />
      </div>

      {/* Save Button */}
      <div className="border-t pt-6 mb-6">
        <Button
          onClick={() => handleSave(onSave)}
          classType="admin"
          className="w-full"
        >
          Save Widget Settings
        </Button>
      </div>
    </div>
  );
};

export default Form_8;
